<script lang="ts" setup>
import OrganizationAvatar from '@/components/utilities/OrganizationAvatar.vue'
import {
    useIsActiveOrganization,
    useOrganizations,
    useActiveOrganization,
    useAuth
} from 'auth-package'
import { watchEffect } from 'vue'
import { useRoute, useRouter } from 'vue-router'

const organizations = useOrganizations()

const activeOrganization = useActiveOrganization()
const route = useRoute()
const router = useRouter()

interface Props {
    avatarSize?: number
    size?: 'small' | 'large'
}

const props = withDefaults(defineProps<Props>(), {
    avatarSize: 40,
    size: 'small'
})

const changeOrganization = (organizationUUId: string | number) => {
    useAuth().setActiveOrganization(organizations.value[organizationUUId])
    const allowedPaths = new Set([
        '/settings/account',
        '/settings/organization',
        '/settings/organization/members'
    ])
    const redirectPath = '/campaign-manager/list/active'
    if (!allowedPaths.has(route.path) && route.path !== redirectPath) {
        router.push(redirectPath)
    }
}

watchEffect(() => {
    const organizationIds = Object.keys(organizations.value)
    if (organizationIds.length === 1 && !activeOrganization.value) {
        useAuth().setActiveOrganization(organizations.value[organizationIds[0]])
    }
})
</script>

<template>
    <el-dropdown
        popper-class="organization-selector-dropdown"
        @command="changeOrganization"
        trigger="click"
    >
        <span class="el-dropdown-link avatar-wrapper">
            <OrganizationAvatar
                v-if="activeOrganization"
                :organization="activeOrganization"
                :tooltipDisabled="true"
                :marginLeft="0"
                :marginRight="0"
            />
            <span
                v-if="props.size == 'large' && activeOrganization"
                class="active-organization-name ms-3"
            >
                {{ activeOrganization.name }}
            </span>
        </span>

        <template #dropdown>
            <el-dropdown-menu>
                <el-dropdown-item
                    v-for="(organization, index) in organizations"
                    :key="index"
                    :command="organization.uuid"
                    :class="[
                        'organization-select-dropdown-item',
                        { active: useIsActiveOrganization(organization).value }
                    ]"
                >
                    <OrganizationAvatar
                        :organization="organization"
                        :isActive="useIsActiveOrganization(organization).value"
                        :tooltipDisabled="true"
                        :marginLeft="0"
                        :marginRight="3"
                    />

                    <el-row :gutter="8">
                        <el-col :span="24">
                            {{ organization.name }}
                        </el-col>

                        <el-col :span="24">
                            {{ organization.description }}
                        </el-col>
                    </el-row>
                </el-dropdown-item>
            </el-dropdown-menu>
        </template>
    </el-dropdown>
</template>

<style lang="scss">
.organization {
    padding-left: 10px;
    padding-right: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.organization-select-dropdown-item {
    display: flex;
    flex-direction: row;

    &.active {
        background-color: var(--el-dropdown-menuItem-hover-fill);
        color: var(--el-dropdown-menuItem-hover-color);
    }
}

.avatar-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;

    .active-organization-name {
        max-width: 180px;
        overflow: hidden;
        width: 145px;
        display: inline-block;
        text-overflow: ellipsis;
    }
}

.el-dropdown-menu {
    max-width: 500px;
    min-width: 300px;
}

.logo-wrapper {
    display: flex;
    align-items: flex-start;
    justify-content: center;
    margin-top: 10px;
}

.organization-details-wrapper {
    margin-top: 8px;
}

.organization-name {
    font-size: var(--el-font-size-base);
    white-space: initial;
}

.organization-desc {
    font-size: var(--el-font-size-small);
    white-space: initial;
}

.organization-selector-item {
    // padding-left: 0 !important;
}
</style>
